import { api } from '../lib/api';
import { isRecord, readStoredJson } from '../lib/persistedState';
import type { CharacterHistoryEntry } from '../types/relationships';
import type { CharacterHistoryTimelineOptions, CharacterTimelineData, TimelineEra, TimelineOccurrence } from '../types/timeline';
import type { EntityDocument, EntityFieldHistoryEntry, EntitySummary, ExplorerNode, PrototypeBlock, ResolvedEntityFieldValues } from '../types/entities';
import type { WorkspaceSummary } from '../types/workspace';

type WorkspaceTimelineStore = {
  workspace: WorkspaceSummary | null;
  entities: EntitySummary[];
  activeEntity: EntityDocument | null;
  explorerNodes: ExplorerNode[];
  prototypeBlocks: PrototypeBlock[];
  timelineOccurrences: TimelineOccurrence[];
  timelineEras: TimelineEra[];
  characterTimeline: CharacterTimelineData | null;
  characterHistory: CharacterHistoryEntry[];
  characterHistoryLoaded: boolean;
  characterHistoryTimelineOptions: CharacterHistoryTimelineOptions;
  entityFieldHistory: EntityFieldHistoryEntry[];
  globalTimeContext: { sceneId: string | null; segmentId: string | null };
  resolvedEntityFieldValues: ResolvedEntityFieldValues | null;
  error: string;
  loadTimeline(): Promise<void>;
  loadCharacterTimeline(): Promise<void>;
  loadCharacterHistory(entityId?: string): Promise<void>;
  loadPrototypeBlocks(): Promise<void>;
  loadEntities(): Promise<void>;
  loadExplorer(): Promise<void>;
  loadEntityFieldHistory(entityId?: string): Promise<void>;
  loadResolvedEntityFieldValues(entityId?: string): Promise<void>;
  setGlobalTimeContext(sceneId: string | null, segmentId?: string | null): Promise<void>;
  resetGlobalTimeContext(): Promise<void>;
  openEntity(id: string, updateHistory?: boolean, force?: boolean, requireClean?: boolean): Promise<EntityDocument | null | undefined>;
  scheduleStoryTimelineRefresh(): void;
};

export function sceneFieldPayload(values: Record<string, unknown>) {
  const { clockTime, chapter: _chapter, chapter_position: _chapterPositionSnake, chapterPosition: _chapterPosition, leadsTo, ...rest } = values;
  return {
    ...rest,
    ...(clockTime !== undefined ? { clock_time: clockTime } : {}),
    ...(leadsTo !== undefined ? { leads_to: leadsTo } : {})
  };
}

const storyTimelineRefreshTimers = new WeakMap<object, ReturnType<typeof setTimeout>>();
const characterTimelineRequests = new WeakMap<object, Map<string, Promise<CharacterTimelineData>>>();
const characterHistoryRequests = new WeakMap<EntityDocument, Promise<void>>();

export function clearWorkspaceTimelineRequests(store: object) {
  const timer = storyTimelineRefreshTimers.get(store);
  if (timer) clearTimeout(timer);
  storyTimelineRefreshTimers.delete(store);
  characterTimelineRequests.delete(store);
}

export const workspaceTimelineActions = {
  clearWorkspaceTimelineRequests(this: WorkspaceTimelineStore) { clearWorkspaceTimelineRequests(this); },
  async loadTimeline(this: WorkspaceTimelineStore) {
    const workspace = this.workspace; if (!workspace) return;
    const response = await api<{ items: TimelineOccurrence[]; eras: TimelineEra[] }>(`/api/v1/workspaces/${workspace.id}/timeline-occurrences`);
    if (this.workspace === workspace) { this.timelineOccurrences = response.items; this.timelineEras = response.eras; }
  },
  async loadCharacterTimeline(this: WorkspaceTimelineStore) {
    if (!this.workspace) return;
    const workspace = this.workspace;
    let requests = characterTimelineRequests.get(this);
    if (!requests) {
      requests = new Map();
      characterTimelineRequests.set(this, requests);
    }
    let request = requests.get(workspace.id);
    if (!request) {
      request = api<CharacterTimelineData>(`/api/v1/workspaces/${workspace.id}/character-timeline`)
        .finally(() => { if (requests.get(workspace.id) === request) requests.delete(workspace.id); });
      requests.set(workspace.id, request);
    }
    const timeline = await request;
    if (this.workspace !== workspace) return;
    this.characterTimeline = timeline;
    if (this.characterHistoryLoaded) this.characterHistoryTimelineOptions = {
      ...this.characterHistoryTimelineOptions,
      chapters: timeline.chapters,
      scenes: timeline.scenes,
      segments: timeline.segments
    };
    if (this.globalTimeContext.sceneId) {
      const sceneExists = this.characterTimeline.scenes.some((scene) => scene.id === this.globalTimeContext.sceneId);
      const segmentExists = !this.globalTimeContext.segmentId || this.characterTimeline.segments.some((segment) => segment.id === this.globalTimeContext.segmentId && segment.sceneId === this.globalTimeContext.sceneId);
      if (!sceneExists || !segmentExists) await this.resetGlobalTimeContext();
    }
  },
  scheduleStoryTimelineRefresh(this: WorkspaceTimelineStore) {
    if (!this.workspace) return;
    const existing = storyTimelineRefreshTimers.get(this);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      storyTimelineRefreshTimers.delete(this);
      void this.loadCharacterTimeline().catch((error) => {
        this.error = error instanceof Error ? error.message : 'Zeitachsen konnten nicht aktualisiert werden.';
      });
    }, 120);
    storyTimelineRefreshTimers.set(this, timer);
  },
  async loadCharacterHistory(this: WorkspaceTimelineStore, entityId?: string) {
    const id = entityId ?? this.activeEntity?.id;
    if (!id) { this.characterHistory = []; this.characterHistoryLoaded = false; this.characterHistoryTimelineOptions = { chapters: [], scenes: [], segments: [], states: [], suggestions: [] }; return; }
    const entity = this.activeEntity;
    if (!entity || entity.id !== id) return;
    const pending = characterHistoryRequests.get(entity);
    if (pending) return pending;
    const request = (async () => {
      if (!this.characterTimeline) await this.loadCharacterTimeline();
      const timeline = this.characterTimeline;
      if (!timeline || this.activeEntity !== entity) return;
      const response = await api<{ items: CharacterHistoryEntry[]; timelineOptions: CharacterHistoryTimelineOptions }>(`/api/v1/entities/${id}/character-history?includeTimeline=false`);
      if (this.activeEntity === entity && entity.id === id) {
        this.characterHistory = response.items;
        this.characterHistoryTimelineOptions = {
          ...response.timelineOptions,
          chapters: timeline.chapters,
          scenes: timeline.scenes,
          segments: timeline.segments
        };
        this.characterHistoryLoaded = true;
      }
    })();
    characterHistoryRequests.set(entity, request);
    try { await request; } finally { characterHistoryRequests.delete(entity); }
  },
  async createCharacterHistory(this: WorkspaceTimelineStore, input: {
    kind: CharacterHistoryEntry['kind']; label: string; value: string; startLabel: string;
    endLabel?: string | null; notes?: string | null; relatedEntityId?: string | null;
    stateEntityId?: string | null; startSceneId?: string | null; startSegmentId?: string | null;
    endSceneId?: string | null; endSegmentId?: string | null
  }) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entities/${this.activeEntity.id}/character-history`, { method: 'POST', body: JSON.stringify(input) });
    await this.loadCharacterHistory();
  },
  async loadPrototypeBlocks(this: WorkspaceTimelineStore) {
    const workspace = this.workspace; if (!workspace) return;
    const response = await api<{ items: PrototypeBlock[] }>(`/api/v1/workspaces/${workspace.id}/prototype-blocks`);
    if (this.workspace === workspace) this.prototypeBlocks = response.items;
  },
  async createPrototypeBlock(this: WorkspaceTimelineStore, input: {
    parentId?: string | null; scopeKind: PrototypeBlock['scopeKind']; scopeEntityId?: string | null;
    content?: string; dateLabel?: string | null; linkedEntityId?: string | null; sortOrder?: number
  }) {
    if (!this.workspace) return;
    const block = await api<PrototypeBlock>(`/api/v1/workspaces/${this.workspace.id}/prototype-blocks`, { method: 'POST', body: JSON.stringify(input) });
    this.prototypeBlocks.push(block);
    return block;
  },
  async updatePrototypeBlock(this: WorkspaceTimelineStore, blockId: string, input: Partial<Pick<PrototypeBlock, 'parentId' | 'content' | 'dateLabel' | 'linkedEntityId' | 'sortOrder'>>) {
    const block = await api<PrototypeBlock>(`/api/v1/prototype-blocks/${blockId}`, { method: 'PATCH', body: JSON.stringify(input) });
    const index = this.prototypeBlocks.findIndex((item) => item.id === blockId);
    if (index >= 0) this.prototypeBlocks[index] = block;
    return block;
  },
  async deletePrototypeBlock(this: WorkspaceTimelineStore, blockId: string) {
    await api(`/api/v1/prototype-blocks/${blockId}`, { method: 'DELETE' });
    await this.loadPrototypeBlocks();
  },
  async convertPrototypeBlock(this: WorkspaceTimelineStore, blockId: string, templateId: string, title?: string) {
    const result = await api<{ block: PrototypeBlock; entityId: string }>(`/api/v1/prototype-blocks/${blockId}/convert`, { method: 'POST', body: JSON.stringify({ templateId, ...(title?.trim() ? { title: title.trim() } : {}) }) });
    const index = this.prototypeBlocks.findIndex((item) => item.id === blockId);
    if (index >= 0) this.prototypeBlocks[index] = result.block;
    await Promise.all([this.loadEntities(), this.loadExplorer()]);
    return result;
  },
  async createTimelineCharacterHistory(this: WorkspaceTimelineStore, entityId: string, input: {
    kind: CharacterHistoryEntry['kind']; label: string; value: string; startLabel: string;
    endLabel?: string | null; notes?: string | null; relatedEntityId?: string | null;
    stateEntityId?: string | null; stateEntityTitle?: string | null; startSceneId?: string | null;
    startSegmentId?: string | null; endSceneId?: string | null; endSegmentId?: string | null
  }) {
    await api(`/api/v1/entities/${entityId}/character-history`, { method: 'POST', body: JSON.stringify(input) });
    await this.loadCharacterTimeline();
    if (this.activeEntity?.id === entityId) await this.loadCharacterHistory(entityId);
  },
  async updateTimelineCharacterHistory(this: WorkspaceTimelineStore, entityId: string, entryId: string, input: {
    kind: CharacterHistoryEntry['kind']; label: string; value: string; startLabel: string;
    endLabel?: string | null; notes?: string | null; relatedEntityId?: string | null;
    stateEntityId?: string | null; stateEntityTitle?: string | null; startSceneId?: string | null;
    startSegmentId?: string | null; endSceneId?: string | null; endSegmentId?: string | null
  }) {
    await api(`/api/v1/entities/${entityId}/character-history/${entryId}`, { method: 'PATCH', body: JSON.stringify(input) });
    await this.loadCharacterTimeline();
    await this.loadEntities();
    if (this.activeEntity?.id === entityId) await this.loadCharacterHistory(entityId);
  },
  async updateCharacterHistory(this: WorkspaceTimelineStore, entryId: string, input: {
    kind: CharacterHistoryEntry['kind']; label: string; value: string; startLabel: string;
    endLabel?: string | null; notes?: string | null; relatedEntityId?: string | null;
    stateEntityId?: string | null; startSceneId?: string | null; startSegmentId?: string | null;
    endSceneId?: string | null; endSegmentId?: string | null
  }) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entities/${this.activeEntity.id}/character-history/${entryId}`, { method: 'PATCH', body: JSON.stringify(input) });
    await this.loadCharacterHistory();
  },
  async deleteCharacterHistory(this: WorkspaceTimelineStore, entryId: string) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entities/${this.activeEntity.id}/character-history/${entryId}`, { method: 'DELETE' });
    await this.loadCharacterHistory();
  },
  async saveTimelineOccurrence(this: WorkspaceTimelineStore, entityId: string, input: {
    eraEntityId: string | null; inUniverseStart: string | null; inUniverseEnd: string | null; sortKey: number;
    narrativePosition: number | null; precision: TimelineOccurrence['precision']; isUncertain: boolean; notes: string | null;
    displayIcon?: string | null; displayColor?: string | null;
    calendarYear?: number | null; calendarPeriod?: number | null; calendarDay?: number | null;
    calendarParts?: Record<string, string | number | null>; clockLabel?: string | null;
    timeBasis?: TimelineOccurrence['timeBasis']; absoluteTime?: string | null;
    timeSource?: TimelineOccurrence['timeSource']; narrativeRelation?: TimelineOccurrence['narrativeRelation'];
    relativeAnchor?: TimelineOccurrence['relativeAnchor']; relativeOffsetMinutes?: number | null
  }) {
    if (!this.workspace) return;
    const current = this.timelineOccurrences.find((item) => item.entityId === entityId);
    if (current) await api(`/api/v1/timeline-occurrences/${current.id}`, { method: 'PATCH', body: JSON.stringify(input) });
    else await api(`/api/v1/workspaces/${this.workspace.id}/timeline-occurrences`, { method: 'POST', body: JSON.stringify({ entityId, ...input }) });
    await Promise.all([this.loadTimeline(), this.loadCharacterTimeline()]);
  },
  async deleteTimelineOccurrence(this: WorkspaceTimelineStore, entityId: string) {
    const current = this.timelineOccurrences.find((item) => item.entityId === entityId);
    if (!current) return;
    await api(`/api/v1/timeline-occurrences/${current.id}`, { method: 'DELETE' });
    await Promise.all([this.loadTimeline(), this.loadCharacterTimeline()]);
  },
  async updateTimelineOccurrence(this: WorkspaceTimelineStore, occurrenceId: string, input: Partial<{
    eraEntityId: string | null; inUniverseStart: string | null; inUniverseEnd: string | null; sortKey: number;
    narrativePosition: number | null; precision: TimelineOccurrence['precision']; isUncertain: boolean; notes: string | null;
    displayIcon: string | null; displayColor: string | null;
    calendarYear: number | null; calendarPeriod: number | null; calendarDay: number | null;
    calendarParts: Record<string, string | number | null>; clockLabel: string | null;
    timeBasis: TimelineOccurrence['timeBasis']; absoluteTime: string | null;
    timeSource: TimelineOccurrence['timeSource']; narrativeRelation: TimelineOccurrence['narrativeRelation'];
    relativeAnchor: TimelineOccurrence['relativeAnchor']; relativeOffsetMinutes: number | null
  }>) {
    await api(`/api/v1/timeline-occurrences/${occurrenceId}`, { method: 'PATCH', body: JSON.stringify(input) });
    await Promise.all([this.loadTimeline(), this.loadCharacterTimeline()]);
  },
  async reorderTimelineOccurrences(this: WorkspaceTimelineStore, updates: Array<{ occurrenceId: string; eraEntityId: string | null; sortKey: number }>) {
    for (const update of updates) await api(`/api/v1/timeline-occurrences/${update.occurrenceId}`, { method: 'PATCH', body: JSON.stringify({ eraEntityId: update.eraEntityId, sortKey: update.sortKey }) });
    await this.loadTimeline();
  },
  async reorderTimelineEras(this: WorkspaceTimelineStore, eraEntityIds: string[]) {
    if (!this.workspace) return;
    await api(`/api/v1/workspaces/${this.workspace.id}/timeline-eras/order`, { method: 'PATCH', body: JSON.stringify({ eraEntityIds }) });
    await this.loadTimeline();
  },
  async reorderSceneTimeline(this: WorkspaceTimelineStore, sceneIds: string[], storyline: string, chapterId = '') {
    if (chapterId) {
      const chapter = await api<EntityDocument>(`/api/v1/entities/${chapterId}`);
      const existing = Array.isArray(chapter.workingVersion.fieldValues.scenes) ? chapter.workingVersion.fieldValues.scenes.map(String) : [];
      const moving = new Set(sceneIds);
      const firstIndex = existing.findIndex((id) => moving.has(id));
      const withoutMoving = existing.filter((id) => !moving.has(id));
      const insertAt = firstIndex < 0 ? withoutMoving.length : Math.min(firstIndex, withoutMoving.length);
      const chapterFields = { ...chapter.workingVersion.fieldValues, scenes: [...withoutMoving.slice(0, insertAt), ...sceneIds, ...withoutMoving.slice(insertAt)] };
      await api(`/api/v1/entities/${chapterId}/working-version`, { method: 'PUT', body: JSON.stringify({ versionId: chapter.workingVersion.id, baseRevision: chapter.workingVersion.revision, title: chapter.title, summary: chapter.summary, contentDoc: chapter.workingVersion.contentDoc, fieldValues: chapterFields, reason: 'manual-flush' }) });
      await this.loadEntities();
      if (this.activeEntity?.id === chapterId) await this.openEntity(chapterId, false, true);
      this.scheduleStoryTimelineRefresh();
      return;
    }
    for (let index = 0; index < sceneIds.length; index += 1) {
      const id = sceneIds[index]!;
      const document = await api<EntityDocument>(`/api/v1/entities/${id}`);
      const fields = sceneFieldPayload({ ...document.workingVersion.fieldValues, storyline });
      await api(`/api/v1/entities/${id}/working-version`, { method: 'PUT', body: JSON.stringify({ versionId: document.workingVersion.id, baseRevision: document.workingVersion.revision, title: document.title, summary: document.summary, contentDoc: document.workingVersion.contentDoc, fieldValues: fields, reason: 'manual-flush' }) });
    }
    await this.loadEntities();
    if (this.activeEntity?.templateCategory === 'szene') await this.openEntity(this.activeEntity.id, false, true);
    this.scheduleStoryTimelineRefresh();
  },
  async loadEntityFieldHistory(this: WorkspaceTimelineStore, entityId?: string) {
    const id = entityId ?? this.activeEntity?.id;
    if (!id) { this.entityFieldHistory = []; return; }
    const entity = this.activeEntity;
    const response = await api<{ items: EntityFieldHistoryEntry[] }>(`/api/v1/entities/${id}/field-history`);
    if (this.activeEntity === entity && entity?.id === id) this.entityFieldHistory = response.items;
  },
  restoreGlobalTimeContext(this: WorkspaceTimelineStore) {
    if (!this.workspace || !this.characterTimeline) return;
    try {
      const saved = readStoredJson(localStorage, `chronis.global-time-context.v1.${this.workspace.id}`);
      const sceneId = isRecord(saved) && typeof saved.sceneId === 'string' ? saved.sceneId : null;
      const segmentId = isRecord(saved) && typeof saved.segmentId === 'string' ? saved.segmentId : null;
      const scene = sceneId ? this.characterTimeline.scenes.find((item) => item.id === sceneId) : null;
      const segment = segmentId ? this.characterTimeline.segments.find((item) => item.id === segmentId && item.sceneId === scene?.id) : null;
      this.globalTimeContext = { sceneId: scene?.id ?? null, segmentId: segment?.id ?? null };
    } catch { this.globalTimeContext = { sceneId: null, segmentId: null }; }
  },
  async loadResolvedEntityFieldValues(this: WorkspaceTimelineStore, entityId?: string) {
    const id = entityId ?? this.activeEntity?.id;
    if (!id) { this.resolvedEntityFieldValues = null; return; }
    const entity = this.activeEntity;
    const params = new URLSearchParams();
    if (this.globalTimeContext.sceneId) params.set('sceneId', this.globalTimeContext.sceneId);
    if (this.globalTimeContext.segmentId) params.set('segmentId', this.globalTimeContext.segmentId);
    const response = await api<ResolvedEntityFieldValues>(`/api/v1/entities/${id}/resolved-field-values${params.size ? `?${params}` : ''}`);
    if (this.activeEntity === entity && entity?.id === id) this.resolvedEntityFieldValues = response;
  },
  async setGlobalTimeContext(this: WorkspaceTimelineStore, sceneId: string | null, segmentId: string | null = null) {
    if (!this.workspace) return;
    this.globalTimeContext = { sceneId, segmentId: sceneId ? segmentId : null };
    localStorage.setItem(`chronis.global-time-context.v1.${this.workspace.id}`, JSON.stringify(this.globalTimeContext));
    await this.loadResolvedEntityFieldValues();
  },
  async resetGlobalTimeContext(this: WorkspaceTimelineStore) { await this.setGlobalTimeContext(null, null); },
  async createEntityFieldHistory(this: WorkspaceTimelineStore, input: {
    fieldKey: string; fieldValue: unknown; startSceneId?: string | null; startSegmentId?: string | null;
    endSceneId?: string | null; endSegmentId?: string | null; notes?: string | null
  }) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entities/${this.activeEntity.id}/field-history`, { method: 'POST', body: JSON.stringify(input) });
    await this.loadEntityFieldHistory();
    await this.loadResolvedEntityFieldValues();
  },
  async updateEntityFieldHistory(this: WorkspaceTimelineStore, entryId: string, input: {
    fieldKey: string; fieldValue: unknown; startSceneId?: string | null; startSegmentId?: string | null;
    endSceneId?: string | null; endSegmentId?: string | null; notes?: string | null
  }) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entities/${this.activeEntity.id}/field-history/${entryId}`, { method: 'PATCH', body: JSON.stringify(input) });
    await this.loadEntityFieldHistory();
    await this.loadResolvedEntityFieldValues();
  },
  async deleteEntityFieldHistory(this: WorkspaceTimelineStore, entryId: string) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entities/${this.activeEntity.id}/field-history/${entryId}`, { method: 'DELETE' });
    await this.loadEntityFieldHistory();
    await this.loadResolvedEntityFieldValues();
  },
  async setSceneStoryline(this: WorkspaceTimelineStore, sceneId: string, storyline: string, chapterId: string) {
    const document = await api<EntityDocument>(`/api/v1/entities/${sceneId}`);
    const fields = sceneFieldPayload({ ...document.workingVersion.fieldValues, storyline });
    await api(`/api/v1/entities/${sceneId}/working-version`, { method: 'PUT', body: JSON.stringify({ versionId: document.workingVersion.id, baseRevision: document.workingVersion.revision, title: document.title, summary: document.summary, contentDoc: document.workingVersion.contentDoc, fieldValues: fields, reason: 'manual-flush' }) });
    await this.loadEntities();
    if (this.activeEntity?.id === chapterId) await this.openEntity(chapterId, false, true);
    this.scheduleStoryTimelineRefresh();
  },
  async updateSceneFromChapter(this: WorkspaceTimelineStore, sceneId: string, patch: { title?: string; summary?: string | null; fieldValues?: Record<string, unknown> }) {
    const document = await api<EntityDocument>(`/api/v1/entities/${sceneId}`);
    const fields = sceneFieldPayload({ ...document.workingVersion.fieldValues, ...(patch.fieldValues ?? {}) });
    await api(`/api/v1/entities/${sceneId}/working-version`, { method: 'PUT', body: JSON.stringify({
      versionId: document.workingVersion.id,
      baseRevision: document.workingVersion.revision,
      title: patch.title ?? document.title,
      summary: patch.summary !== undefined ? patch.summary : document.summary,
      contentDoc: document.workingVersion.contentDoc,
      fieldValues: fields,
      reason: 'manual-flush'
    }) });
    await this.loadEntities();
    this.scheduleStoryTimelineRefresh();
  }
};
